import { useMutation, useQuery } from '@apollo/client/react'
import { useCallback } from 'react'
import { DISMISS_OBJECT, QUERY_ACCOUNT } from '../graphql/account'

type DismissedObjectInput = { id: string; expiresAt?: string }

type UseDismissObjectsReturn = {
  onDismissObjects: (dismissedObjects: DismissedObjectInput[]) => void
}

/**
 * Batch variant of `useDismissObject` for surfaces that dismiss several
 * objects in one interaction (e.g. one dismissed object per active billing
 * condition). The dismissed objects are provided at call time, not hook time.
 */
const useDismissObjects = ({
  organizationId,
}: {
  organizationId?: string
}): UseDismissObjectsReturn => {
  const { data } = useQuery(QUERY_ACCOUNT, { fetchPolicy: 'cache-only' })
  const [dismissObject] = useMutation(DISMISS_OBJECT)

  const onDismissObjects = useCallback(
    (dismissedObjects: DismissedObjectInput[]) => {
      if (!organizationId) {
        return
      }

      // allSettled gives every mutation promise a rejection handler; a failed
      // dismissal is non-critical and resurfaces the banner on next load.
      void Promise.allSettled(
        dismissedObjects.map((dismissedObject) =>
          dismissObject({
            variables: { organizationId, dismissedObject },
            update: (cache) => {
              const existing = data?.account?.currentOrganization
              if (!existing) return

              cache.modify({
                id: cache.identify(existing),
                fields: {
                  dismissedObjects(existingObjects = [], { toReference }) {
                    return [
                      ...existingObjects,
                      toReference(
                        {
                          __typename: 'DismissedObject',
                          id: dismissedObject.id,
                        },
                        true,
                      ),
                    ]
                  },
                },
              })
            },
          }),
        ),
      )
    },
    [organizationId, dismissObject, data],
  )

  return { onDismissObjects }
}

export default useDismissObjects
